import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Plus, Edit, Trash2, User, GraduationCap, MapPin, Calendar, Landmark,
  Phone, Mail, Camera, Upload, X, Search
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const emptyRp = {
  name: '', photo: null, designation: '', expertise: '',
  mobile: '', email: '', address: '',
  bank_name: '', bank_account_no: '', ifsc_code: '',
  pan_number: '', aadhaar_number: '',
  remarks: '',
};

// Module-level cache so re-opening dialogs / re-rendering cards is instant.
const photoCache = new Map();

const emptyTraining = {
  date: new Date().toISOString().split('T')[0],
  end_date: '',
  no_of_days: 1,
  honorarium: 0,
  location: '',
  training_topic: '',
  branch_id: '',
  remarks: '',
};

const inr = (n) => `Rs.${(n || 0).toLocaleString('en-IN')}`;

// Lazy photo loader — fetches the photo on demand and caches in the parent map.
// Falls back to the User icon while loading / when missing.
const RpPhoto = ({ rpId, hasPhoto, className = '', alt = 'RP' }) => {
  const [src, setSrc] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!rpId || !hasPhoto) return;
    if (photoCache.has(rpId)) {
      const cached = photoCache.get(rpId);
      if (cached) setSrc(cached);
      return;
    }
    // Fire the lightweight photo endpoint
    api.get(`/resource-persons/${rpId}/photo`)
      .then((r) => {
        if (cancelled) return;
        const p = r.data?.photo || null;
        photoCache.set(rpId, p);
        if (p) setSrc(p); else setErrored(true);
      })
      .catch(() => { if (!cancelled) setErrored(true); });
    return () => { cancelled = true; };
  }, [rpId, hasPhoto]);

  if (src && !errored) {
    return <img src={src} alt={alt} className={className} />;
  }
  return <User className="w-1/2 h-1/2 text-slate-300" />;
};

// Module-level cache so re-opening dialogs / re-rendering cards is instant


// ---------- RP Form Dialog ----------
const RpFormDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(emptyRp);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (open) {
      if (editing) {
        setForm({ ...emptyRp, ...editing, photo: null });
        // Photo not present in the list row (light payload) — lazy fetch it
        if (editing.has_photo) {
          if (photoCache.has(editing.id)) {
            setPhotoPreview(photoCache.get(editing.id) || null);
          } else {
            api.get(`/resource-persons/${editing.id}/photo`)
              .then((r) => {
                if (cancelled) return;
                const p = r.data?.photo || null;
                photoCache.set(editing.id, p);
                setPhotoPreview(p);
              })
              .catch(() => { if (!cancelled) setPhotoPreview(null); });
          }
        } else {
          setPhotoPreview(null);
        }
      } else {
        setForm(emptyRp);
        setPhotoPreview(null);
      }
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open, editing?.id]);

  const handlePhoto = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Photo must be under 3 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setForm((f) => ({ ...f, photo: e.target.result }));
      setPhotoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await api.put(`/resource-persons/${editing.id}`, form);
        // Invalidate cached photo if the user re-uploaded
        if (form.photo) photoCache.delete(editing.id);
        toast.success('Resource Person updated');
      } else {
        await api.post('/resource-persons', form);
        toast.success('Resource Person added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Resource Person' : 'Add Resource Person'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {/* Photo */}
          <div className="flex items-center gap-4">
            <div className="w-24 h-28 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
              {photoPreview ? (
                <img src={photoPreview} alt="RP" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-slate-300" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handlePhoto(e.target.files?.[0])}
                data-testid="rp-photo-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1.5" /> Upload Photo
              </Button>
              {photoPreview && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setForm((f) => ({ ...f, photo: '' })); setPhotoPreview(null); }}
                  className="text-red-600"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>

          {/* Basic */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="rp-name" />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={form.designation || ''} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g., Senior Trainer" />
            </div>
            <div className="md:col-span-2">
              <Label>Area of Expertise</Label>
              <Input value={form.expertise || ''} onChange={(e) => setForm({ ...form, expertise: e.target.value })} placeholder="e.g., Financial Literacy, Skill Training" />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={form.mobile || ''} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Textarea rows={2} value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>

          {/* Bank */}
          <div>
            <h3 className="text-sm font-semibold text-emerald-800 flex items-center gap-2 mb-2">
              <Landmark className="w-4 h-4" /> Bank / KYC Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Bank Name</Label>
                <Input value={form.bank_name || ''} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input value={form.bank_account_no || ''} onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })} />
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input value={form.ifsc_code || ''} onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>PAN Number</Label>
                <Input value={form.pan_number || ''} onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })} />
              </div>
              <div className="md:col-span-2">
                <Label>Aadhaar Number</Label>
                <Input value={form.aadhaar_number || ''} onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value })} />
              </div>
            </div>
          </div>

          <div>
            <Label>Remarks</Label>
            <Textarea rows={2} value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="rp-save-btn">
              {editing ? 'Update' : 'Add Resource Person'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Training Form Dialog ----------
const TrainingFormDialog = ({ open, onOpenChange, rpId, editing, branches, onSaved }) => {
  const [form, setForm] = useState(emptyTraining);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          date: editing.date ? new Date(editing.date).toISOString().split('T')[0] : '',
          end_date: editing.end_date ? new Date(editing.end_date).toISOString().split('T')[0] : '',
          no_of_days: editing.no_of_days || 1,
          honorarium: editing.honorarium || 0,
          location: editing.location || '',
          training_topic: editing.training_topic || '',
          branch_id: editing.branch_id || '',
          remarks: editing.remarks || '',
        });
      } else {
        setForm(emptyTraining);
      }
    }
    // eslint-disable-next-line
  }, [open, editing?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.location.trim()) { toast.error('Location is required'); return; }
    if (!form.date) { toast.error('Start date is required'); return; }
    try {
      const payload = {
        ...form,
        no_of_days: parseInt(form.no_of_days) || 1,
        honorarium: parseFloat(form.honorarium) || 0,
        date: new Date(form.date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        branch_id: form.branch_id || null,
      };
      if (editing) {
        await api.put(`/rp-trainings/${editing.id}`, payload);
        toast.success('Training updated');
      } else {
        await api.post(`/resource-persons/${rpId}/trainings`, payload);
        toast.success('Training added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Training Record' : 'Add Training Record'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Start Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="tr-date" />
            </div>
            <div>
              <Label>End Date (optional)</Label>
              <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div>
              <Label>No. of Days *</Label>
              <Input type="number" min="1" value={form.no_of_days} onChange={(e) => setForm({ ...form, no_of_days: e.target.value })} required data-testid="tr-days" />
            </div>
            <div>
              <Label>Honorarium (Rs.) *</Label>
              <Input type="number" min="0" step="0.01" value={form.honorarium} onChange={(e) => setForm({ ...form, honorarium: e.target.value })} required data-testid="tr-honorarium" />
            </div>
            <div className="md:col-span-2">
              <Label>Location *</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required placeholder="e.g., WWAGS HQ, Wangjing" data-testid="tr-location" />
            </div>
            <div className="md:col-span-2">
              <Label>Training Topic</Label>
              <Input value={form.training_topic || ''} onChange={(e) => setForm({ ...form, training_topic: e.target.value })} placeholder="e.g., Cash Book Management" />
            </div>
            <div className="md:col-span-2">
              <Label>Host Block (optional)</Label>
              <Select value={form.branch_id || 'none'} onValueChange={(v) => setForm({ ...form, branch_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="tr-branch"><SelectValue placeholder="Select block" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Remarks</Label>
              <Textarea rows={2} value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="tr-save-btn">
              {editing ? 'Update' : 'Add Training'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ---------- RP Detail Dialog ----------
const RpDetailDialog = ({ open, onOpenChange, rp, isAdmin: isAdminUser }) => {
  const [full, setFull] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [trainingDlg, setTrainingDlg] = useState({ open: false, editing: null });
  const [deleteDlg, setDeleteDlg] = useState({ open: false, item: null });

  const load = useCallback(async () => {
    if (!rp?.id) return;
    // Skip re-fetching the full RP — the row from the list already has every
    // field (except the heavy Base64 photo, which the RpPhoto sub-component
    // fetches lazily and caches).
    setFull(rp);
    try {
      const [trResp, brResp] = await Promise.all([
        api.get(`/resource-persons/${rp.id}/trainings`),
        api.get('/branches'),
      ]);
      setTrainings(trResp.data || []);
      setBranches(brResp.data || []);
    } catch {
      toast.error('Failed to load RP details');
    }
  }, [rp]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const doDelete = async () => {
    try {
      await api.delete(`/rp-trainings/${deleteDlg.item.id}`);
      toast.success('Training deleted');
      setDeleteDlg({ open: false, item: null });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (!rp) return null;

  const totalDays = trainings.reduce((s, t) => s + (t.no_of_days || 0), 0);
  const totalHon = trainings.reduce((s, t) => s + (t.honorarium || 0), 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resource Person — {rp.name}</DialogTitle>
          </DialogHeader>

          {!full ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin h-8 w-8 border-b-2 border-emerald-700 rounded-full"></div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Profile card */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 flex flex-col md:flex-row gap-4">
                <div className="w-28 h-32 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0 flex items-center justify-center">
                  <RpPhoto rpId={full.id} hasPhoto={full.has_photo} alt={full.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-slate-500">Designation:</span> <b className="text-slate-800">{full.designation || '—'}</b></div>
                  <div><span className="text-slate-500">Expertise:</span> <b className="text-slate-800">{full.expertise || '—'}</b></div>
                  <div className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" /> <b>{full.mobile || '—'}</b></div>
                  <div className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-slate-400" /> <b>{full.email || '—'}</b></div>
                  <div className="md:col-span-2"><span className="text-slate-500">Address:</span> {full.address || '—'}</div>
                </div>
              </div>

              {/* Bank */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
                  <Landmark className="w-4 h-4 text-emerald-700" /> Bank / KYC
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div><p className="text-xs text-slate-500">Bank Name</p><p className="font-medium">{full.bank_name || '—'}</p></div>
                  <div><p className="text-xs text-slate-500">Account No.</p><p className="font-mono font-medium">{full.bank_account_no || '—'}</p></div>
                  <div><p className="text-xs text-slate-500">IFSC</p><p className="font-mono font-medium">{full.ifsc_code || '—'}</p></div>
                  <div><p className="text-xs text-slate-500">PAN</p><p className="font-mono font-medium">{full.pan_number || '—'}</p></div>
                  <div className="col-span-2"><p className="text-xs text-slate-500">Aadhaar</p><p className="font-mono font-medium">{full.aadhaar_number || '—'}</p></div>
                </div>
              </div>

              {/* Trainings */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-emerald-700" /> Trainings ({trainings.length})
                  </h3>
                  <div className="flex items-center gap-4 text-sm">
                    <div><span className="text-slate-500">Total Days: </span><b>{totalDays}</b></div>
                    <div><span className="text-slate-500">Total Honorarium: </span><b className="text-emerald-800">{inr(totalHon)}</b></div>
                    {isAdminUser && (
                      <Button
                        size="sm"
                        onClick={() => setTrainingDlg({ open: true, editing: null })}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid="add-training-btn"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add Training
                      </Button>
                    )}
                  </div>
                </div>

                {trainings.length === 0 ? (
                  <p className="text-sm text-slate-400 italic text-center py-6">No training records yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs uppercase font-semibold text-slate-600">Date</th>
                          <th className="px-3 py-2 text-left text-xs uppercase font-semibold text-slate-600">Topic</th>
                          <th className="px-3 py-2 text-left text-xs uppercase font-semibold text-slate-600">Location</th>
                          <th className="px-3 py-2 text-left text-xs uppercase font-semibold text-slate-600">Host Block</th>
                          <th className="px-3 py-2 text-right text-xs uppercase font-semibold text-slate-600">Days</th>
                          <th className="px-3 py-2 text-right text-xs uppercase font-semibold text-slate-600">Honorarium</th>
                          {isAdminUser && <th className="px-3 py-2"></th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {trainings.map((t) => (
                          <tr key={t.id} className="hover:bg-slate-50" data-testid="training-row">
                            <td className="px-3 py-2">
                              {t.date ? new Date(t.date).toLocaleDateString('en-IN') : '—'}
                              {t.end_date && ` – ${new Date(t.end_date).toLocaleDateString('en-IN')}`}
                            </td>
                            <td className="px-3 py-2">{t.training_topic || '—'}</td>
                            <td className="px-3 py-2 flex items-center gap-1 text-slate-700"><MapPin className="w-3 h-3 text-slate-400" />{t.location}</td>
                            <td className="px-3 py-2 text-slate-600">{t.branch_name || '—'}</td>
                            <td className="px-3 py-2 text-right">{t.no_of_days}</td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-800">{inr(t.honorarium)}</td>
                            {isAdminUser && (
                              <td className="px-3 py-2 text-right">
                                <Button variant="ghost" size="sm" onClick={() => setTrainingDlg({ open: true, editing: t })} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50">
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setDeleteDlg({ open: true, item: t })} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t font-semibold">
                        <tr>
                          <td colSpan={4} className="px-3 py-2 text-right text-slate-700">Totals:</td>
                          <td className="px-3 py-2 text-right">{totalDays}</td>
                          <td className="px-3 py-2 text-right text-emerald-800">{inr(totalHon)}</td>
                          {isAdminUser && <td></td>}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {full.remarks && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Remarks</p>
                  <p className="text-sm text-slate-700">{full.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TrainingFormDialog
        open={trainingDlg.open}
        onOpenChange={(o) => setTrainingDlg({ open: o, editing: o ? trainingDlg.editing : null })}
        rpId={rp.id}
        editing={trainingDlg.editing}
        branches={branches}
        onSaved={() => { setTrainingDlg({ open: false, editing: null }); load(); }}
      />

      <DeleteConfirmDialog
        open={deleteDlg.open}
        onOpenChange={(o) => setDeleteDlg({ open: o, item: o ? deleteDlg.item : null })}
        onConfirm={doDelete}
        title="Delete Training"
        description="This action cannot be undone."
        itemName={deleteDlg.item?.training_topic || deleteDlg.item?.location || ''}
      />
    </>
  );
};

// ---------- Main Page ----------
const ResourcePersons = () => {
  const { isAdmin } = useAuth();
  const [rps, setRps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rpDlg, setRpDlg] = useState({ open: false, editing: null });
  const [detailDlg, setDetailDlg] = useState({ open: false, rp: null });
  const [deleteDlg, setDeleteDlg] = useState({ open: false, item: null });

  const fetchRps = async () => {
    setLoading(true);
    try {
      // Light payload — no inline photos. Photos are lazy-loaded per card.
      const r = await api.get('/resource-persons', { params: { include_photo: false } });
      setRps(r.data || []);
    } catch {
      toast.error('Failed to load Resource Persons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRps(); }, []);

  const doDeleteRp = async () => {
    try {
      await api.delete(`/resource-persons/${deleteDlg.item.id}`);
      photoCache.delete(deleteDlg.item.id);
      toast.success('Resource Person deleted');
      setDeleteDlg({ open: false, item: null });
      fetchRps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = rps.filter((rp) =>
    !search ||
    rp.name?.toLowerCase().includes(search.toLowerCase()) ||
    rp.expertise?.toLowerCase().includes(search.toLowerCase()) ||
    rp.mobile?.includes(search)
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="resource-persons-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Resource Persons</h1>
          <p className="text-lg text-slate-600">Trainers, mentors & their training records</p>
        </div>
        {isAdmin() && (
          <Button
            onClick={() => setRpDlg({ open: true, editing: null })}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="add-rp-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Resource Person
          </Button>
        )}
      </div>

      <div className="mb-6 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, expertise, mobile…"
          className="pl-9"
          data-testid="rp-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-3" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Resource Persons yet</h3>
          <p className="text-slate-500">{isAdmin() ? 'Click "Add Resource Person" to create one.' : 'Ask an Admin to add trainers.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((rp) => (
            <div
              key={rp.id}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer"
              onClick={() => setDetailDlg({ open: true, rp })}
              data-testid="rp-card"
            >
              <div className="flex items-start gap-3">
                <div className="w-16 h-20 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center">
                  <RpPhoto rpId={rp.id} hasPhoto={rp.has_photo} alt={rp.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{rp.name}</h3>
                  <p className="text-xs text-slate-500 truncate">{rp.designation || rp.expertise || 'Trainer'}</p>
                  {rp.mobile && <p className="text-xs text-slate-600 mt-1 flex items-center gap-1"><Phone className="w-3 h-3" />{rp.mobile}</p>}
                </div>
                {isAdmin() && (
                  <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => setRpDlg({ open: true, editing: rp })} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50">
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteDlg({ open: true, item: rp })} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-center">
                <div>
                  <p className="text-lg font-bold text-emerald-700">{rp.training_count || 0}</p>
                  <p className="text-[10px] uppercase text-slate-500">Trainings</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-blue-700">{rp.total_days || 0}</p>
                  <p className="text-[10px] uppercase text-slate-500">Days</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-orange-700">{inr(rp.total_honorarium)}</p>
                  <p className="text-[10px] uppercase text-slate-500">Honorarium</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <RpFormDialog
        open={rpDlg.open}
        onOpenChange={(o) => setRpDlg({ open: o, editing: o ? rpDlg.editing : null })}
        editing={rpDlg.editing}
        onSaved={() => { setRpDlg({ open: false, editing: null }); fetchRps(); }}
      />
      <RpDetailDialog
        open={detailDlg.open}
        onOpenChange={(o) => setDetailDlg({ open: o, rp: o ? detailDlg.rp : null })}
        rp={detailDlg.rp}
        isAdmin={isAdmin()}
      />
      <DeleteConfirmDialog
        open={deleteDlg.open}
        onOpenChange={(o) => setDeleteDlg({ open: o, item: o ? deleteDlg.item : null })}
        onConfirm={doDeleteRp}
        title="Delete Resource Person"
        description="This will also delete all training records for this person. This action cannot be undone."
        itemName={deleteDlg.item?.name || ''}
      />
    </div>
  );
};

export default ResourcePersons;
